import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AutoComplete as AntAutoComplete } from 'antd';
import { autoCompleteInput } from '../../utils/autoComplete';

//#region
const CustomAutoComplete = styled(AntAutoComplete)`
  ${props =>
    props.$alert &&
    `
    &:not(.ant-select-customize-input) .ant-select-selector {
        border: 1px solid var(--default-500-danger);
    }
`}
`;
//#endregion

const { Option } = AntAutoComplete;
const AutoComplete = ({ value, placeholder, onChange, alert, clinicId, size, indicesId }) => {
  const [options, setOptions] = useState([]);
  const [query, setQuery] = useState(value);
  const abortRef = useRef(null);

  const debouncedFetchRef = useRef(
    debounce(
      (value, clinicId, indicesId, callback, signal) => {
        autoCompleteInput(value, clinicId, indicesId, callback, signal);
      },
      500,
      { leading: false, trailing: true },
    ),
  );

  useEffect(() => {
    debouncedFetchRef.current.cancel();
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const callback = ({ hits }) => setOptions(hits);

    if (query === '') {
      autoCompleteInput(query, clinicId, indicesId, callback, controller.signal);
    } else {
      debouncedFetchRef.current(query, clinicId, indicesId, callback, controller.signal);
    }
  }, [query, clinicId, indicesId]);

  useEffect(() => {
    const debouncedFn = debouncedFetchRef.current;
    return () => {
      abortRef.current?.abort();
      debouncedFn.cancel();
    };
  }, []);

  return (
    <CustomAutoComplete
      size={size}
      placeholder={placeholder}
      onChange={value => onChange(value)}
      value={value}
      onSearch={value => setQuery(value)}
      filterOption={false} // ant can filter twice
      $alert={alert}
    >
      {options.map(({ project, objectID }) => (
        <Option key={objectID} value={project}>
          {project}
        </Option>
      ))}
    </CustomAutoComplete>
  );
};

export default AutoComplete;

AutoComplete.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  alert: PropTypes.bool,
  size: PropTypes.string,
  clinic_id: PropTypes.string,
};

AutoComplete.defaultTypes = {
  value: '',
  onChange: () => {},
  placeholder: '',
  alert: false,
  size: 'large',
  clinic_id: '',
};
